import * as React from 'react';
import { Modal } from 'rcd-cpp-ui/ui/modal/Modal';
import { Primary, Buttons, Cancel } from 'rcd-cpp-ui/ui/button';
import { Field } from '../../MetadataForm/metadataFormField';
import { useActions } from './stagedTagsFormActions';
import { useAlert } from '../../Alert/alert';
import { useLoader } from '../../Loader/loader';
import { useSelector } from '../../../utils/useSelector';
import { AdminDomain } from '../../../enums/AdminDomain';
import { ButtonDropdown } from '../../Dropdown/dropdown';
import { NodeType } from '../../../enums/NodeType';
import { findOne } from 'rcd-cpp-workspace/utils/array';
import { Metadata } from '../../../types/Metadata';
import { Export } from '../../View/Export/export';
import { FileUpload } from '../../FileUpload/fileUpload';
import { useToaster } from '../../Toaster/toaster';
import { BacktagImport } from '../../../api/backtag';
import { FormUpdater } from '../../../types/Field';
import { Icon, Refresh } from 'rcd-cpp-ui/ui/icon';

enum Opt {
  Publish = 'Publish',
  Export = 'Export',
  Import = 'Import'
}

const STAGED_FILE_TYPES = ['.csv'];

export const StagedTagsActions = () => {
  const dataSchema = useSelector(s => s.view.selected.dataSchema);
  const [showForm, setShowForm] = React.useState(false);
  const [showImport, setShowImport] = React.useState(false);
  const { reloadData } = useActions();

  const options = React.useMemo(() => {
    const opts = [Opt.Publish, Opt.Export];
    if (process.env.ENV != 'prod') {
      opts.push(Opt.Import);
    }
    return opts;
  }, []);
  const onSelect = React.useCallback((o: Opt) => {
    switch (o) {
      case Opt.Publish:
        setShowForm(true);
        break;
      case Opt.Import:
        setShowImport(true);
        break;
    }
  }, []);

  if (dataSchema?.id != AdminDomain.stagedTags) return null;
  return (
    <div>
      <Icon onClick={reloadData}>
        <Refresh />
      </Icon>
      <ButtonDropdown
        label="Actions"
        className="view-button"
        options={options}
        onSelect={onSelect}
        position="right"
        renderOption={o => {
          if (o == Opt.Export) return <Export viewName="Staged Items" />;
          return o;
        }}
      />
      {showForm && <StagedTagsForm onClose={() => setShowForm(false)} />}
      {showImport && <ImportModal onClose={() => setShowImport(false)} />}
    </div>
  );
};

const Header = {
  Done: 'Imported',
  Failed: 'Failed to import'
};
export const ImportToaster: React.FC<Metadata> = (m: Metadata) => {
  const {
    queryCount,
    queryExecuted,
    fileName,
    message,
    status,
    importedItems,
    importedTCs,
    importedTTs,
    importedRPs,
    importedJSs,
    totalItems,
    totalTCs,
    totalTTs,
    totalRPs,
    totalJSs
  } = m?.props || {};
  const header = Header[status] || 'Importing';
  const width = Math.round((queryExecuted / queryCount) * 100) + '%';
  const [showProgress, setShowProgress] = React.useState(true);
  React.useEffect(() => {
    let to = null;
    if (['Failed', 'Done'].includes(status)) {
      to = setTimeout(() => {
        setShowProgress(false);
      }, 1000);
    }
    return () => clearTimeout(to);
  }, [status]);
  return (
    <div>
      <div>
        <b>
          {header} {fileName}
        </b>
      </div>
      {message && <div>{message}</div>}
      {!showProgress && totalItems && (
        <div>
          <div>
            {importedItems} out of {totalItems} Staged Items
          </div>
          {totalTCs > 0 && (
            <div>
              {importedTCs} out of {totalTCs} Staged Topic Categories
            </div>
          )}
          {totalTTs > 0 && (
            <div>
              {importedTTs} out of {totalTTs} Perennial Priorities
            </div>
          )}
          {totalRPs > 0 && (
            <div>
              {importedRPs} out of {totalRPs} Staged Mission-Critical Priorities
            </div>
          )}
          {totalJSs > 0 && (
            <div>
              {importedJSs} out of {totalJSs} Staged Journey Steps
            </div>
          )}
        </div>
      )}
      {!!queryCount && showProgress && (
        <div className="progress" style={{ height: '5px' }}>
          <div className="progress-bar" style={{ width }} />
        </div>
      )}
    </div>
  );
};

type ValidationResult = {
  backtagData: any[];
  report: { [x: string]: number };
  backtagImport: BacktagImport;
};

const ImportModal = ({ onClose }) => {
  const [file, setFile] = React.useState<File>(null);
  const [validationResult, setValidationResult] =
    React.useState<ValidationResult>(null);
  const { getStagedTagsItemCount, ...actions } = useActions();
  const {
    isLoading,
    Loader,
    Alert,
    setMessage,
    importBacktagData,
    validateFile
  } = useAlert(useLoader(actions));
  const toaster = useToaster(ImportToaster);
  React.useEffect(() => {
    setValidationResult(null);
    setMessage(null);
    if (file) {
      validateFile(file, STAGED_FILE_TYPES).then(r => {
        setValidationResult(r);
      });
    }
  }, [file]);
  return (
    <Modal
      id="backtagImportModal"
      onClose={onClose}
      header="Import"
      footer={
        <Buttons>
          <Cancel onClick={onClose} />
          <Primary
            label="Import"
            onClick={() => {
              if (validationResult?.backtagData?.length) {
                importBacktagData(
                  validationResult.backtagImport,
                  validationResult.backtagData,
                  toaster,
                  onClose
                );
              }
            }}
            disabled={!validationResult?.backtagData?.length || isLoading}
          />
        </Buttons>
      }
    >
      <div className="metadata-form scrollable">
        <Alert />
        <Loader />
        <FileUpload
          disabled={isLoading}
          fileTypes={STAGED_FILE_TYPES}
          uploadFile={setFile}
          buttonLabel="Select File"
          fileNamePlaceholder="Choose a CSV file"
        />
        {!!validationResult?.report && (
          <div>
            {Object.entries(validationResult.report)
              .filter(([k, v]) => v !== null)
              .map(([k, v]) => (
                <div key={k}>
                  <label>{k}:&nbsp;</label>
                  {v}
                </div>
              ))}
          </div>
        )}
      </div>
    </Modal>
  );
};

type Count = {
  count: number;
  curTimeInMs: number;
};

const StagedTagsForm = ({ onClose }) => {
  const schema = useSelector(s => s.view?.selected?.dataSchema);
  const [data, setData] = React.useState<Partial<Metadata>>({ props: {} });

  const fieldsNode = [NodeType.TaxonomyTag, NodeType.RolePriority];
  const [count, setCount] = React.useState(null);

  const { getStagedTagsItemCount, ...actions } = useActions();
  const { isLoading, Loader, Alert, publishStagedTags } = useAlert(
    useLoader(actions)
  );
  const updater: FormUpdater = {
    update: () => {},
    reset: () => {},
    updateField: () => {},
    updateFields: fields => {
      setData(d => {
        fields.forEach(({ field, value }) => {
          d.props[field.field] = value;
        });
        return { ...d, props: { ...d.props } };
      });
    }
  };
  React.useEffect(() => {
    const { rolePriorities, taxonomyTags } = data.props;
    if (rolePriorities?.length || taxonomyTags?.length) {
      getStagedTagsItemCount(data).then((response: Count) => {
        setCount(response.count);
      });
    } else {
      setCount(null);
    }
  }, [data]);

  const fields = React.useMemo(
    () =>
      fieldsNode.map(nt => findOne(schema?.props?.fields, { dataType: nt })),
    [schema?.props?.fields]
  );
  return (
    <Modal
      id="backtagModal"
      onClose={onClose}
      header="Publish Staged"
      footer={
        <Buttons>
          <Cancel onClick={onClose} disabled={isLoading} />
          <Primary
            label="Publish"
            onClick={() => {
              publishStagedTags(data).then(r => {
                onClose();
              });
            }}
            disabled={
              isLoading ||
              (!data?.props?.taxonomyTags?.length &&
                !data?.props?.rolePriorities?.length)
            }
          />
        </Buttons>
      }
    >
      <Loader />
      <div className="metadata-form scrollable">
        <Alert />
        {fields.map((field, index) => {
          return (
            <Field
              key={field.field}
              field={field}
              data={data}
              schema={schema}
              disabled={isLoading}
              updater={updater}
              componentRight={() =>
                count && index == 0 ? (
                  <span>
                    Total <b>{count}</b> of items tagged
                  </span>
                ) : null
              }
            />
          );
        })}
      </div>
    </Modal>
  );
};
